import { AnalyticsEventTopic } from '~/modules/analytics/analyticsHelper';
import { AI_OVERVIEW_EVENT_NAME, DataCloudEventTypeValues } from '~/modules/analytics/datacloudEventTypes/constants';
import { DataCloudEventType, DataCloudIdentityData } from '~/modules/analytics/datacloudEventTypes/datacloudEventType';
import { FeatureFlags } from '~/modules/feature_flags';
import { DataCloudEventData } from '~/modules/services/analyticsService';
import { randomUUID } from '~/modules/utilities/cross_env_utils';

const FEEDBACK_REASON_MAX = 200;

export type AiOverviewFeedbackRating = 'up' | 'down';

export type AiOverviewFeedbackStatus = 'idle' | 'streaming' | 'done' | 'truncated' | 'error';

export interface AiOverviewFeedbackDataCloudAnalyticsEvent {
    interaction: {
        name: typeof AI_OVERVIEW_EVENT_NAME;
        eventType: typeof DataCloudEventTypeValues.aiOverviewFeedback;
        deviceId: string;
        searchQueryUid: string;
        'co-relation-id': string;
        aiOverviewStatus: AiOverviewFeedbackStatus;
        aiOverviewListingCount: number;
        aiOverviewFeedback: AiOverviewFeedbackRating;
        anonymous?: '0' | '1';
        language?: string;
        userAgent?: string;
        source?: string;
        searchString?: string;
        TokenCount?: number;
        TTFT?: number;
        aiOverviewFeedbackReason?: string;
    };
    user: {
        attributes: DataCloudIdentityData & {
            eventType: string;
            deviceId: string;
        };
    };
}

export interface AiOverviewFeedbackEventData extends DataCloudEventData {
    aiOverviewFeedback?: AiOverviewFeedbackRating;
    aiOverviewStatus?: AiOverviewFeedbackStatus;
    aiOverviewListingCount?: number;
    aiOverviewTokenCount?: number;
    aiOverviewTtft?: number;
    aiOverviewFeedbackReason?: string;
    aiOverviewCorrelationId?: string;
    searchQueryUid?: string;
    searchString?: string;
    source?: string;
}

const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));

const isFiniteNumber = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

const normalizeReason = (raw: string): string =>
    raw
        .replace(/[\r\n]+/g, ' ')
        .trim()
        .slice(0, FEEDBACK_REASON_MAX);

export class AiOverviewFeedbackDataCloudEventType extends DataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        if (!FeatureFlags.FF_ACE_ENABLE_AI_LISTINGS_SUMMARY) {
            return false;
        }
        return event.topic === AnalyticsEventTopic.ANALYTICS_AI_OVERVIEW_FEEDBACK;
    }

    getBody(event: AiOverviewFeedbackEventData): AiOverviewFeedbackDataCloudAnalyticsEvent {
        const baseEvent = this.baseBody(event);
        const rating: AiOverviewFeedbackRating = event.aiOverviewFeedback === 'down' ? 'down' : 'up';
        const status: AiOverviewFeedbackStatus = event.aiOverviewStatus ?? 'idle';
        const listingCount = isFiniteNumber(event.aiOverviewListingCount)
            ? Math.max(0, event.aiOverviewListingCount)
            : 0;

        const interaction: AiOverviewFeedbackDataCloudAnalyticsEvent['interaction'] = {
            name: AI_OVERVIEW_EVENT_NAME,
            eventType: DataCloudEventTypeValues.aiOverviewFeedback,
            deviceId: baseEvent.username,
            searchQueryUid: event.searchQueryUid ?? '',
            'co-relation-id': event.aiOverviewCorrelationId || randomUUID(),
            aiOverviewStatus: status,
            aiOverviewListingCount: listingCount,
            aiOverviewFeedback: rating,
            anonymous: baseEvent.anonymous ? '1' : '0',
            language: baseEvent.language,
            userAgent: baseEvent.userAgent,
        };

        const source = event.source?.trim();
        if (source) {
            interaction.source = source;
        }

        const searchString = event.searchString?.trim();
        if (searchString) {
            interaction.searchString = searchString;
        }

        if (isFiniteNumber(event.aiOverviewTokenCount)) {
            interaction.TokenCount = clamp(event.aiOverviewTokenCount, 0, 100_000);
        }

        if (isFiniteNumber(event.aiOverviewTtft)) {
            interaction.TTFT = clamp(event.aiOverviewTtft, 0, 60_000);
        }

        if (event.aiOverviewFeedbackReason) {
            const reason = normalizeReason(event.aiOverviewFeedbackReason);
            if (reason) {
                interaction.aiOverviewFeedbackReason = reason;
            }
        }

        return {
            interaction,
            user: {
                attributes: {
                    ...baseEvent.userData,
                    eventType: DataCloudEventTypeValues.identity,
                    deviceId: baseEvent.username,
                },
            },
        };
    }
}
